import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import { useDataProvider, useResourceContext } from 'react-admin';
import CosmosPlayer from 'components/CosmosPlayer/CosmosPlayer';
import { PlayerMetaType } from 'components/CosmosPlayer/types';
import { SearchContextType } from 'components/Search/types';
import { GenericSearchItem, getRecordRealId } from '../../SearchItemsProvider';
import { Box, CardMedia, Stack, Typography, useTheme } from '@mui/material';
import { bloxValues } from 'themes/constants';
import { IconHelper } from 'components/generic/IconHelper';
import { PreviewImage } from './SearchImagePreview';
import InvalidSourceOverlay from './InvalidSourceOverlay';
import { BloxCustomDataProviderType } from 'utility/types/dataProvider';
import { useTranslation } from 'react-i18next';

type Props = {
  view: SearchContextType['view'];
  record: GenericSearchItem;
  previewImageUrl?: string;
  previewWidth?: string;
  previewHeight?: string;
  isHovered?: boolean;
};

/**
 * Renders Card Media, Summary Card, preview images or videos.
 * @param param0
 * @returns
 */
const SearchMediaPreview = ({
  view,
  record,
  previewImageUrl,
  previewWidth = '172px',
  previewHeight = '100%',
  isHovered = false,
}: Props) => {
  const theme = useTheme();
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const resource = useResourceContext();
  const [videoMeta, setVideoMeta] = useState<PlayerMetaType | undefined>(undefined);
  const [debouncedHovered] = useDebouncedValue(isHovered, 1000);
  const recordId = getRecordRealId(record, resource);
  const title = record?.title || '';
  const assetType = record?.type || '';
  const { t } = useTranslation();

  // Only fetch if it's a video, has an ID, is hovered, and we haven't already loaded the metadata
  const shouldFetch =
    assetType === 'video' && !!recordId && debouncedHovered && !videoMeta;

  const { data: videoAsset } = useQuery({
    queryKey: ['getEditorialAssetById', recordId],
    enabled: shouldFetch,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: Infinity,
    gcTime: Infinity,
    queryFn: async () =>
      await dataProvider.getEditorialAssetById({
        assetId: recordId,
      }),
  });

  useEffect(() => {
    if (videoAsset?.data && !videoMeta) {
      const asset = videoAsset?.data;

      // Determine the source based on available data
      let source: string;
      if (asset.remote_video_player?.includes('iframe')) {
        const iframe = document.createElement('div');
        iframe.innerHTML = asset.remote_video_player;
        source = iframe.querySelector('iframe')?.getAttribute('src') || '';
      } else {
        source =
          asset.files?.video?.url ||
          asset.remote_video?.properties?.mp4_url ||
          asset.remote_video?.properties?.url ||
          '';
      }

      // Create a video-specific metadata object
      const videoMetadata: PlayerMetaType = {
        uniqueId: (asset.uuid || asset.id).toString(),
        type: 'video',
        title: asset.title || 'Video File',
        alt: asset.title || '',
        source,
      };

      setVideoMeta(videoMetadata);
    }
  }, [videoAsset, videoMeta]);

  // Validate that the resolved source URL is usable before swapping to the player
  const isValidSource =
    !!videoMeta?.source &&
    (videoMeta.source.startsWith('http') || videoMeta.source.startsWith('blob'));

  // Determine whether to show video player
  const showVideoPlayer = debouncedHovered && videoMeta && isValidSource;

  // Show an overlay when we have metadata but the source is invalid
  const showInvalidSourceOverlay = debouncedHovered && videoMeta && !isValidSource;

  // ========================================
  // CARD view
  // ========================================
  if (view === 'card') {
    return (
      <Box
        data-testid="card-media-box-wrapper"
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
        }}
      >
        {showVideoPlayer ? (
          <CosmosPlayer
            playerMeta={videoMeta}
            width="100%"
            height="100%"
            playing={true}
            controls={false}
            muted={true}
            // Prevents video player from being interactive in search previews
            wrapperSx={{ pointerEvents: 'none' }}
          />
        ) : previewImageUrl ? (
          <>
            <CardMedia
              id="card-media"
              data-testid="card-media"
              component="img"
              draggable="false"
              src={previewImageUrl}
              alt={title}
              loading="eager"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            {showInvalidSourceOverlay && <InvalidSourceOverlay />}
          </>
        ) : (
          <>
            <Stack
              id="no-preview-available-stack"
              data-testid="no-preview-available-stack"
              direction="column"
              sx={{
                alignItems: 'center',
                height: '100%',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: bloxValues.regularFontSizeRem,
                  fontWeight: 400,
                  color: theme.palette.action.disabled,
                }}
              >
                {t('blox.general.no_preview')}
              </Typography>
              <IconHelper
                iconType={assetType}
                svgIconProps={{
                  fontSize: 'large',
                  style: { fill: theme.palette.action.disabled },
                }}
              />
            </Stack>
            {showInvalidSourceOverlay && <InvalidSourceOverlay />}
          </>
        )}
      </Box>
    );
  }

  // ========================================
  // SUMMARY view
  // ========================================
  return (
    <>
      {showVideoPlayer ? (
        <CosmosPlayer
          playerMeta={videoMeta}
          width={previewWidth}
          height={previewHeight}
          playing={true}
          controls={false}
          muted={true}
          // Prevents video player from being interactive in search previews
          wrapperSx={{ pointerEvents: 'none' }}
        />
      ) : (
        <Box
          sx={{
            position: 'relative',
            width: previewWidth,
            height: previewHeight,
          }}
        >
          <PreviewImage
            previewUrl={previewImageUrl}
            title={title}
            type={assetType}
            previewWidth={previewWidth}
            previewHeight={previewHeight}
          />
          {showInvalidSourceOverlay && <InvalidSourceOverlay />}
        </Box>
      )}
    </>
  );
};

export default SearchMediaPreview;
